import * as React from 'react';

import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router';

import * as AppConstant from 'constants/app';

import { AccountActionCreators, checkLoginStatus } from 'actions/account';
import { getWalletAccount } from 'actions/wallet';
import { AsyncRoute } from 'components/AsyncRoute';
import { LocaleProvider } from 'components/LocaleProvider';
import { ScrollToTop } from 'components/ScrollToTop';
import { WalletFreeze } from 'components/Wallet';
import { Layout } from 'containers/Layout';
import { isAuthenticated } from 'helper/auth';
import { Account } from 'models/account';
import { AppState } from 'models/app';
import { WalletAccountType } from 'models/wallet';

interface StateProps {
  account: Account;
  isAuthenticated: boolean;
  walletAccountType: WalletAccountType;
}

interface DispatchProps {
  checkLoginStatus: typeof checkLoginStatus;
  getWalletAccount: typeof getWalletAccount;
  setIsAuthenticated: typeof AccountActionCreators.setIsAuthenticated.create;
}

type OwnProps = RouteComponentProps<{}>;
type AppProps = StateProps & DispatchProps & OwnProps;

const loaders = {
  Account: () => import('containers/Account'),
  AccountGetStarted: () => import('components/AccountGetStarted'),
  Checkout: () => import('containers/Checkout'),
  CheckoutComponent: () => import('components/Checkout'),
  Static: () => import('containers/Static'),
  StaticComponent: () => import('components/Static'),
  Verification: () => import('containers/Verification'),
  Wallet: () => import('containers/Wallet'),
  WalletTransferQrCode: () => import('components/Wallet/WalletTransferQrCode/WalletTransferQrCode')
};

class AppContainer extends React.Component<AppProps, {}> {
  constructor(props: AppProps) {
    super(props);

    this.Checkout = this.Checkout.bind(this);
    this.WalletRedeem = this.WalletRedeem.bind(this);
    this.WalletTransfer = this.WalletTransfer.bind(this);
  }

  componentDidMount() {
    if (this.isAuthenticated) {
      this.props.setIsAuthenticated();
      this.props.checkLoginStatus();
      this.props.getWalletAccount();
    }
  }

  render() {
    const { location } = this.props;
    return (
      <ScrollToTop location={location}>
        <LocaleProvider location={location}>
          <Switch>
            <Route path={AppConstant.ROUTE_CHECKOUT} component={this.Checkout}/>
            <Route path={AppConstant.ROUTE_STATIC} component={this.Static}/>
            <Route path={AppConstant.ROUTE_VERIFICATION} component={this.Verification}/>

            {!this.isAuthenticated && <AsyncRoute exact={true} path="/" loader={loaders.Static} name="StaticLanding" key="StaticLanding"/>}

            {this.isAuthenticated ? this.authRoutes : this.unAuthRoutes}
          </Switch>
        </LocaleProvider>
      </ScrollToTop>
    );
  }

  get isAuthenticated() {
    return this.props.isAuthenticated || isAuthenticated();
  }

  get unAuthRoutes() {
    return (
      <Layout history={this.props.history}>
        <Switch>
          <AsyncRoute path={AppConstant.ROUTE_FORGOT_PASSWORD_OTP} loader={loaders.Account} name="AccountForgotPasswordOtp" key="AccountForgotPasswordOtp"/>
          <AsyncRoute path={AppConstant.ROUTE_FORGOT_PASSWORD} loader={loaders.Account} name="AccountForgotPassword" key="AccountForgotPassword"/>
          <AsyncRoute path={AppConstant.ROUTE_LOGIN} loader={loaders.Account} name="AccountLogin" key="AccountLogin"/>
          <Redirect to={AppConstant.ROUTE_LOGIN} />
        </Switch>
      </Layout>
    );
  }

  get authRoutes() {
    return (
      <Layout history={this.props.history} modifier="off-white">
        <Switch>
          <Route path={AppConstant.ROUTE_FREEZE_DETAIL} component={WalletFreeze}/>

          <AsyncRoute exact={true} path={AppConstant.ROUTE_ACCOUNT_PHONE_DETAIL} loader={loaders.Account} name="AccountPhoneDetail" key="AccountPhoneDetail"/>
          <AsyncRoute path={AppConstant.ROUTE_ACCOUNT_PHONE_SET_OTP} loader={loaders.Account} name="AccountPhoneSetOtp" key="AccountPhoneSetOtp"/>
          <AsyncRoute path={AppConstant.ROUTE_ACCOUNT_PHONE_SET} loader={loaders.Account} name="AccountPhoneSet" key="AccountPhoneSet"/>
          <AsyncRoute path={AppConstant.ROUTE_ACCOUNT_EMAIL_UNVERIFIED} loader={loaders.Account} name="AccountEmailUnverified" key="AccountEmailUnverified"/>
          <AsyncRoute path={AppConstant.ROUTE_ACCOUNT_EMAIL_UPDATE} loader={loaders.Account} name="AccountEmailUpdate" key="AccountEmailUpdate"/>
          <AsyncRoute path={AppConstant.ROUTE_ACCOUNT_PASSWORD_UPDATE} loader={loaders.Account} name="AccountPasswordUpdate" key="AccountPasswordUpdate"/>
          <AsyncRoute exact={true} path={AppConstant.ROUTE_ACCOUNT_PROFILE} loader={loaders.Account} name="AccountProfile" key="AccountProfile"/>
          <AsyncRoute path={AppConstant.ROUTE_ACCOUNT_PROFILE_UPDATE} loader={loaders.Account} name="AccountProfileUpdate" key="AccountProfileUpdate"/>
          <AsyncRoute path={AppConstant.ROUTE_ACCOUNT_STORE_REGISTER} loader={loaders.Account} name="AccountStoreRegister" key="AccountStoreRegister"/>
          <AsyncRoute path={AppConstant.ROUTE_ACCOUNT_STORE_UPDATE} loader={loaders.Account} name="AccountStoreUpdate" key="AccountStoreUpdate"/>

          <AsyncRoute path={AppConstant.ROUTE_WALLET_DASHBOARD} loader={loaders.Wallet} name="WalletDashboard" key="WalletDashboard"/>
          <AsyncRoute path={AppConstant.ROUTE_WALLET_TOPUP_ERROR} loader={loaders.Wallet} name="WalletTopUpError" key="WalletTopUpError"/>
          <Route path={AppConstant.ROUTE_WALLET_REDEEM} component={this.WalletRedeem}/>
          <Redirect from={AppConstant.ROUTE_WALLET} to={AppConstant.ROUTE_WALLET_DASHBOARD}/>

          <AsyncRoute path={AppConstant.ROUTE_ACCOUNT_GET_STARTED} loader={loaders.AccountGetStarted} name="AccountGetStarted" key="AccountGetStarted"/>

          <Redirect to={AppConstant.ROUTE_WALLET_DASHBOARD}/>
        </Switch>
      </Layout>
    );
  }

  private Checkout(props: RouteComponentProps<{}>) {
    return (
      <Layout history={props.history} modifier="off-white">
        <Switch>
          <AsyncRoute path={AppConstant.ROUTE_CHECKOUT_ERROR} loader={loaders.CheckoutComponent} name="CheckoutError" key="CheckoutError"/>
          <AsyncRoute path={AppConstant.ROUTE_CHECKOUT_PREVIEW} loader={loaders.Checkout} name="CheckoutPreview" key="CheckoutPreview"/>
          <Redirect to={AppConstant.ROUTE_CHECKOUT_ERROR}/>
        </Switch>
      </Layout>
    );
  }

  private Static(props: RouteComponentProps<{}>) {
    return (
      <Layout history={props.history} modifier="off-white">
        <Switch>
          <AsyncRoute path={AppConstant.ROUTE_STATIC_CONTACT_US} loader={loaders.StaticComponent} name="StaticContactUs" key="StaticContactUs"/>
          <AsyncRoute path={AppConstant.ROUTE_STATIC_FAQ} loader={loaders.StaticComponent} name="StaticFaq" key="StaticFaq"/>
          <AsyncRoute path={AppConstant.ROUTE_STATIC_TERM} loader={loaders.StaticComponent} name="StaticTerm" key="StaticTerm"/>
          <Redirect to={AppConstant.ROUTE_LOGIN}/>
        </Switch>
      </Layout>
    );
  }

  private Verification(props: RouteComponentProps<{}>) {
    return (
      <Layout history={props.history} modifier="off-white">
        <Switch>
          <AsyncRoute path={AppConstant.ROUTE_VERIFICATION_FORGOT_PASSWORD_EMAIL} loader={loaders.Verification} name="VerificationForgotPasswordByEmail" key="VerificationForgotPasswordByEmail"/>
          <AsyncRoute path={AppConstant.ROUTE_VERIFICATION_EMAIL_ADDRESS} loader={loaders.Verification} name="VerificationEmailAddress" key="VerificationEmailAddress"/>
          <Redirect to={AppConstant.ROUTE_LOGIN}/>
        </Switch>
      </Layout>
    );
  }

  private WalletRedeem(props: RouteComponentProps<{}>) {
    const { walletAccountType } = this.props;
    if (walletAccountType.id && !walletAccountType.allowRedeem) {
      return <Redirect to={AppConstant.ROUTE_WALLET_DASHBOARD}/>;
    }

    return (
      <Switch>
        <AsyncRoute path={AppConstant.ROUTE_WALLET_REDEEM_ACCOUNT_HISTORY} loader={loaders.Wallet} name="WalletRedeemAccountHistory" key="WalletRedeemAccountHistory"/>
        <AsyncRoute path={AppConstant.ROUTE_WALLET_REDEEM_ACCOUNT_ADD} loader={loaders.Wallet} name="WalletRedeemAccountAdd" key="WalletRedeemAccountAdd"/>
        <AsyncRoute path={AppConstant.ROUTE_WALLET_REDEEM_AMOUNT} loader={loaders.Wallet} name="WalletRedeemAmount" key="WalletRedeemAmount"/>
        <AsyncRoute path={AppConstant.ROUTE_WALLET_REDEEM_FINISH} loader={loaders.Wallet} name="WalletRedeemFinish" key="WalletRedeemFinish"/>
        <AsyncRoute path={AppConstant.ROUTE_WALLET_REDEEM_OTP} loader={loaders.Wallet} name="WalletRedeemOtp" key="WalletRedeemOtp"/>
        <Redirect to={AppConstant.ROUTE_WALLET_DASHBOARD}/>
      </Switch>
    );
  }

  private WalletTransfer(props: RouteComponentProps<{}>) {
    const { walletAccountType } = this.props;
    if (walletAccountType.id && !walletAccountType.allowTransfer) {
      return <Redirect to={AppConstant.ROUTE_WALLET_DASHBOARD}/>;
    }

    return (
      <Switch>
        <AsyncRoute path={AppConstant.ROUTE_WALLET_TRANSFER_AMOUNT} loader={loaders.Wallet} name="WalletTransferAmount" key="WalletTransferAmount"/>
        <AsyncRoute path={AppConstant.ROUTE_WALLET_TRANSFER_PHONE_WITH_PARAM} loader={loaders.Wallet} name="WalletTransferPhone" key="WalletTransferPhone"/>
        <AsyncRoute path={AppConstant.ROUTE_WALLET_TRANSFER_PHONE} loader={loaders.Wallet} name="WalletTransferPhone" key="WalletTransferPhone"/>
        <AsyncRoute path={AppConstant.ROUTE_WALLET_TRANSFER_OTP} loader={loaders.Wallet} name="WalletTransferOtp" key="WalletTransferOtp"/>
        <AsyncRoute path={AppConstant.ROUTE_WALLET_TRANSFER_FINISH} loader={loaders.Wallet} name="WalletTransferFinish" key="WalletTransferFinish"/>
        <AsyncRoute path={AppConstant.ROUTE_WALLET_TRANSFER_QR_CODE} loader={loaders.WalletTransferQrCode} name="WalletTransferQrCode" key="WalletTransferQrCode"/>
        <Redirect to={AppConstant.ROUTE_WALLET_DASHBOARD}/>
      </Switch>
    );
  }
}

export const App = connect<StateProps, DispatchProps, OwnProps>(
  (state: AppState) => ({
    account: state.accountState.account,
    isAuthenticated: state.accountState.isAuthenticated,
    walletAccountType: state.walletState.accountState.walletAccount.type
  }), {
    checkLoginStatus,
    getWalletAccount,
    setIsAuthenticated: AccountActionCreators.setIsAuthenticated.create
  }
)(AppContainer);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/App.tsx